<template>
  <div v-if="pageError" class="min-h-screen bg-slate-900 flex items-center justify-center p-4">
    <div class="bg-red-500/10 border border-red-500/20 rounded-xl p-8 max-w-md w-full text-center">
      <h2 class="text-xl font-bold text-red-400 mb-4">Page Error</h2>
      <p class="text-red-300 mb-6">{{ pageError }}</p>
      <button @click="reloadPage" class="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl transition-colors">
        Reload Page
      </button>
    </div>
  </div>
  <div v-else class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
    <!-- Animated Background -->
    <div class="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
    <div class="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-emerald-900/20 via-transparent to-transparent"></div>
    
    <!-- Animated Grid -->
    <div class="absolute inset-0 bg-grid-pattern opacity-10"></div>
    
    <!-- Floating Particles -->
    <div class="absolute inset-0">
      <div class="particle particle-1"></div>
      <div class="particle particle-2"></div>
      <div class="particle particle-3"></div>
      <div class="particle particle-4"></div>
      <div class="particle particle-5"></div>
    </div>

    <!-- Main Content -->
    <div class="relative z-10 min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div class="w-full max-w-md">
        <!-- Logo and Header -->
        <div class="text-center mb-8">
          <!-- Logo -->
          <div class="flex items-center justify-center space-x-3 mb-8">
            <div class="w-12 h-12 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/25">
              <svg class="w-7 h-7 text-slate-900" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 2a8 8 0 100 16 8 8 0 000-16zM8 14a6 6 0 1112 0H8z"/>
              </svg>
            </div>
            <span class="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              OilBuddy
            </span>
            <span class="px-2 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-xs text-emerald-400 font-medium">
              AI
            </span>
          </div>

          <!-- Admin Badge -->
          <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
            <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
            Admin Access
          </div>

          <h1 class="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            Welcome Back
          </h1>
          
          <p class="text-lg text-slate-300 mb-8">
            Enter your credentials to access the admin panel
          </p>
        </div>

        <!-- Login Card -->
        <div class="bg-white/5 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/10">
          <form @submit.prevent="login" class="space-y-6">
            <div>
              <label for="email" class="block text-sm font-medium text-slate-300 mb-3">
                Email Address
              </label>
              <input
                id="email"
                v-model="form.email"
                type="email"
                required
                class="w-full px-4 py-4 bg-slate-800/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300 backdrop-blur-sm"
                placeholder="Email address"
              />
            </div>

            <div>
              <label for="password" class="block text-sm font-medium text-slate-300 mb-3">
                Password
              </label>
              <input
                id="password"
                v-model="form.password"
                type="password"
                required
                class="w-full px-4 py-4 bg-slate-800/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300 backdrop-blur-sm"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              :disabled="isLoading"
              class="btn-hero-primary w-full justify-center"
            >
              <svg v-if="isLoading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ isLoading ? 'Signing in...' : 'Access Admin Panel' }}
              <svg v-if="!isLoading" class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
              </svg>
            </button>

            <div v-if="error" class="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
              <p class="text-red-400 text-sm text-center">{{ error }}</p>
            </div>
          </form>
        </div>

        <!-- Back to Site -->
        <div class="text-center mt-8">
          <a
            href="/#/login"
            class="text-slate-400 hover:text-emerald-400 text-sm transition-colors duration-300 inline-flex items-center font-medium"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
            </svg>
            User sign in
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAdminStore } from '../../stores/adminStore'
import { reactive, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const adminStore = useAdminStore()
const isLoading = ref(false)
const error = ref('')
const pageError = ref<string>('')

const form = reactive({
  email: '',
  password: ''
})

const handlePageError = (error: any) => {
  console.error('AdminLoginPage error:', error)
  pageError.value = error.message || 'An error occurred while loading the admin login page'
}

const reloadPage = () => {
  window.location.reload()
}

const login = async () => {
  try {
    isLoading.value = true
    error.value = ''

    const result = await adminStore.login(form.email, form.password)
    
    if (result.success) {
      // Add a small delay to ensure state is updated
      setTimeout(() => {
        router.push('/admin')
      }, 100)
    } else {
      error.value = result.error || 'An error occurred during login. Please try again.'
    }
  } catch (err) {
    console.error('Login error:', err)
    error.value = 'An unexpected error occurred. Please try again.'
  } finally {
    isLoading.value = false
  }
}

// Add error handling on component mount
onMounted(() => {
  try {
    console.log('AdminLoginPage mounted successfully')
  } catch (err) {
    handlePageError(err)
  }
})
</script>

<style scoped>
/* Background patterns and animations */
.bg-grid-pattern {
  background-image: radial-gradient(circle, rgba(148, 163, 184, 0.3) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: grid-move 20s linear infinite;
}

@keyframes grid-move {
  0% { transform: translate(0, 0); }
  100% { transform: translate(50px, 50px); }
}

/* Floating particles */
.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: linear-gradient(45deg, #10b981, #06b6d4);
  border-radius: 50%;
  animation: float 6s ease-in-out infinite;
}

.particle-1 { top: 20%; left: 10%; animation-delay: 0s; }
.particle-2 { top: 60%; left: 20%; animation-delay: 1s; }
.particle-3 { top: 40%; left: 80%; animation-delay: 2s; }
.particle-4 { top: 80%; left: 70%; animation-delay: 3s; }
.particle-5 { top: 30%; left: 60%; animation-delay: 4s; }

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.6; }
  50% { transform: translateY(-20px) rotate(180deg); opacity: 1; }
}

/* Hero button styles */
.btn-hero-primary {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 1rem 2rem;
  background: linear-gradient(to right, #10b981, #06b6d4);
  color: white;
  font-weight: bold;
  border-radius: 1rem;
  font-size: 1.125rem;
  transition: all 0.3s ease;
  transform: scale(1);
  box-shadow: 0 25px 50px -12px rgba(16, 185, 129, 0.25);
  overflow: hidden;
}

.btn-hero-primary:hover {
  transform: scale(1.05);
}

.btn-hero-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: scale(1);
}
</style>
